import LocalStorage from './LocalStorage';

/**
 * These are the log methods we support on this class.
 */
export type LOG_LEVEL = "info" | "warn" | "error" | "debug" | "trace";

/**
 * The default log level we'll be using.
 */
const DEFAULT_LOG_LEVEL: LOG_LEVEL = "info";

/**
 * Bits for each level.
 */
const LOG_LEVEL_INFO  = 0b00001;
const LOG_LEVEL_WARN  = 0b00010;
const LOG_LEVEL_ERROR = 0b00100;
const LOG_LEVEL_DEBUG = 0b01000;
const LOG_LEVEL_TRACE = 0b10000;

/**
 * A map of log level to which methods should actually be printed.
 */
const LOG_LEVEL_BITMASK: {[key in LOG_LEVEL]: number} = {
  trace: LOG_LEVEL_INFO | LOG_LEVEL_WARN | LOG_LEVEL_ERROR | LOG_LEVEL_DEBUG | LOG_LEVEL_TRACE,
  debug: LOG_LEVEL_INFO | LOG_LEVEL_WARN | LOG_LEVEL_ERROR | LOG_LEVEL_DEBUG,
  info: LOG_LEVEL_INFO | LOG_LEVEL_WARN | LOG_LEVEL_ERROR,
  warn: LOG_LEVEL_WARN | LOG_LEVEL_ERROR,
  error: LOG_LEVEL_ERROR,
};

/**
 * A map of log level to the integer representation.
 */
const LOG_LEVEL_MAP: {[key in LOG_LEVEL]: number} = {
  trace: LOG_LEVEL_TRACE,
  debug: LOG_LEVEL_DEBUG,
  error: LOG_LEVEL_ERROR,
  warn: LOG_LEVEL_WARN,
  info: LOG_LEVEL_INFO,
};

/**
 * The key we'll cache the log level value in.
 */
const LOCAL_STORAGE_KEY = "_ONESIGNAL_SHOPIFY_LOG_LEVEL_";

/**
 * By default, we need to have no-op functions on the static methods to ensure
 * we don't crash if the log level isn't set before invoking those functions.
 */
const NO_OP: Function = (): void => {
  return;
};

export default class Log {
  static debug: Function = NO_OP;
  static trace: Function = NO_OP;
  static info: Function = NO_OP;
  static warn: Function = NO_OP;
  static error: Function = NO_OP;

  /**
   * The current log level cached in memory.
   */
  private static _level?: LOG_LEVEL;
  
  /**
   * Set and cache the log level.
   * 
   * @param level value to set the log level to.
   */
  public static setLevel(level: LOG_LEVEL): void {
    this._level = level;
    LocalStorage.set(LOCAL_STORAGE_KEY, level);
    this.createProxyMethods();
  }

  /**
   * Get the log level.
   */
  public static getLevel(): LOG_LEVEL {
    if (typeof this._level === "undefined") {
      const value = LocalStorage.get(LOCAL_STORAGE_KEY);  
      this._level = (value as LOG_LEVEL | null) || DEFAULT_LOG_LEVEL;
      this.createProxyMethods();
    }

    return this._level;
  }

  /**
   * Bind each of the methods to the native methods.
   */
  private static createProxyMethods(): void {
    // create the proxy methods
    this.createProxyMethod("trace", window.console.trace);
    this.createProxyMethod("debug", window.console.log);
    this.createProxyMethod("error", window.console.error);
    this.createProxyMethod("warn", window.console.warn);
    this.createProxyMethod("info", window.console.info);
  }

  /**
   * Bind the function on this class to the native method.
   * 
   * @param level The current log level we're attemting to proxy.
   * @param native The native function to call.
   */
  public static createProxyMethod(level: LOG_LEVEL, native?: Function): void {
    if (this.logLevelEnabled(level) && typeof native !== "undefined") {
      this[level] = native.bind(console);
    } else {
      this[level] = NO_OP;
    }
  }

  /**
   * Determine if the provided log level should log determined by the current log level.
   * 
   * @param level the log level we're checking against.
   */
  private static logLevelEnabled(level: LOG_LEVEL): boolean {
    const currentLevel = this.getLevel();
    return (LOG_LEVEL_BITMASK[currentLevel] & LOG_LEVEL_MAP[level]) == LOG_LEVEL_MAP[level];
  }
}
